import { defineStore } from 'pinia';
import type { LogState, LogMessage, LogType, NotificationMessage } from '@lambda-libs/types';

export const useLogStore = defineStore('logs', {
  state: (): LogState => ({
    messages: [],
    maxMessages: 500,
    isPopupVisible: false
  }),

  actions: {
    log(...args: any[]): void {
      // For the addMessage, we'll join multiple arguments into a string if needed
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      this.addMessage({ message, type: 'info' });
    },
    info(...args: any[]): void {
      // For the addMessage, we'll join multiple arguments into a string if needed
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      this.addMessage({ message, type: 'info' });
    },
    error(...args: any[]): void {
      console.error(...args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      this.addMessage({ message, type: 'error' });
    },
    addMessage(message: NotificationMessage | string): void {
      const logEntry: LogMessage = {
        id: Date.now(),
        timestamp: new Date(),
        message: typeof message === 'string' ? message : message.message,
        type: (typeof message === 'string' ? 'info' : message.type) as LogType || 'info'
      };

      this.messages.push(logEntry);

      // Remove oldest messages if we exceed maxMessages
      if (this.messages.length > this.maxMessages) {
        this.messages = this.messages.slice(-this.maxMessages);
      }
    },

    clearMessages(): void {
      this.messages = [];
    },

    removeMessage(id: number): void {
      const index = this.messages.findIndex(msg => msg.id === id);
      if (index !== -1) {
        this.messages.splice(index, 1);
      }
    },

    togglePopup(): void {
      this.isPopupVisible = !this.isPopupVisible;
    },

    showPopup(): void {
      this.isPopupVisible = true;
    },

    hidePopup(): void {
      this.isPopupVisible = false;
    }
  },

  getters: {
    messageCount(): number {
      return this.messages.length;
    },
    
    sortedMessages(): LogMessage[] {
      return [...this.messages].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    },
    
    messagesByType: (state) => {
      return (type: LogType): LogMessage[] => 
        state.messages.filter(msg => msg.type === type);
    },
    
    hasMessages(): boolean {
      return this.messages.length > 0;
    }
  }
});